import { useQuery } from '@tanstack/react-query';
import axios from 'constants/axios';
import type { AxiosResponse, AxiosError } from 'axios';

export interface GeoLocation {
  countryCode: string;
  countryName: string;
}

interface GeoLocationApiResponse {
  country_code: string;
  country_name: string;
}

interface GeoLocationError {
  message: string;
  status?: number;
  isServiceUnavailable?: boolean;
}

interface UseGeoLocationProps {
  onSuccess?: (data: GeoLocation) => void;
  onError?: (error: GeoLocationError) => void;
  enabled?: boolean;
}

const getGeoLocationKey = () => ['geoLocation'];

export const useGeoLocation = ({
  onSuccess,
  onError,
  enabled = true,
}: UseGeoLocationProps = {}) => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: getGeoLocationKey(),
    queryFn: async () => {
      try {
        const res: AxiosResponse<GeoLocationApiResponse> = await axios.get(
          '/public/geo'
        );

        return {
          countryCode: res.data.country_code,
          countryName: res.data.country_name,
        };
      } catch (err) {
        const axiosError = err as AxiosError;
        const responseData = axiosError.response?.data;

        const errorMessage =
          typeof responseData === 'string'
            ? responseData
            : axiosError.message || 'Failed to fetch geo location';

        const errorInfo: GeoLocationError = {
          message: errorMessage,
          status: axiosError.response?.status,
          isServiceUnavailable: axiosError.response?.status === 503,
        };

        const errorToThrow = new Error(errorInfo.message);
        Object.assign(errorToThrow, errorInfo);
        throw errorToThrow;
      }
    },
    onSuccess: (responseData) => {
      onSuccess?.(responseData);
    },
    onError: (responseError: GeoLocationError) => {
      onError?.(responseError);
    },
    enabled,
  });

  const geoLocation = data || { countryCode: '', countryName: 'Unknown' };

  return {
    geoLocation,
    isLoading,
    isError,
    error: error as GeoLocationError | null,
  };
};
